import React from 'react';
import { LinkedInAd } from '../types/dataTypes';
import { Lightbulb, Target, Layout, Globe, MessageSquare, TrendingUp } from 'lucide-react';

interface AdAnalysisSummaryProps {
  ads: LinkedInAd[];
}

// Enhanced theme detection with categorization
const extractThemes = (text: string): string[] => {
  const themes: Set<string> = new Set();
  if (!text) return [];

  const themePatterns = {
    'AI/ML': ['ai', 'intelligence', 'machine learning', 'ml', 'neural', 'predictive'],
    'Analytics': ['analytics', 'insight', 'metrics', 'dashboard', 'reporting', 'tracking'],
    'Data Management': ['data', 'database', 'storage', 'warehouse', 'lake'],
    'Cloud Solutions': ['cloud', 'saas', 'platform', 'infrastructure', 'serverless'],
    'Trust & Security': ['trust', 'security', 'compliance', 'privacy', 'protection'],
    'Cost Efficiency': ['cost', 'efficiency', 'savings', 'roi', 'investment', 'budget'],
    'Integration': ['integration', 'connect', 'api', 'workflow', 'ecosystem'],
    'Automation': ['automation', 'workflow', 'streamline', 'process'],
    'Real-time': ['real-time', 'instant', 'live', 'dynamic'],
    'Enterprise Scale': ['scale', 'enterprise', 'global', 'growth'],
    'Compliance': ['compliance', 'governance', 'regulation', 'standard'],
    'ROI Focus': ['roi', 'revenue', 'profit', 'growth', 'results'],
    'Innovation': ['innovation', 'cutting-edge', 'revolutionary', 'breakthrough'],
    'Customer Success': ['success', 'satisfaction', 'experience', 'support'],
    'Digital Transformation': ['transformation', 'digital', 'modernization', 'future']
  };

  const textLower = text.toLowerCase();
  
  Object.entries(themePatterns).forEach(([theme, keywords]) => {
    if (keywords.some(keyword => textLower.includes(keyword))) {
      themes.add(theme);
    }
  });

  return Array.from(themes);
};

// Analyze headline patterns
const analyzeHeadlinePatterns = (headline: string): string[] => {
  const patterns: string[] = [];
  const headlineLower = headline.toLowerCase();

  if (headline.includes('?')) patterns.push('Question-based');
  if (/\d+/.test(headline)) patterns.push('Numbers/Statistics');
  if (/now|today|limited|exclusive|special/i.test(headline)) patterns.push('Urgency');
  if (/free|save|bonus|extra/i.test(headline)) patterns.push('Value Proposition');
  if (/how|why|what|when/i.test(headline)) patterns.push('Educational');
  if (/you|your/i.test(headline)) patterns.push('Personal Appeal');
  if (/new|introducing|announcing/i.test(headline)) patterns.push('News/Announcement');
  if (/guide|ebook|whitepaper|report/i.test(headline)) patterns.push('Content Offer');
  
  return patterns;
};

// Analyze copy strategies
const analyzeCopyStrategies = (body: string): string[] => {
  const strategies: string[] = [];
  const bodyLower = body.toLowerCase();

  if (/problem|challenge|struggle|difficult/i.test(body)) strategies.push('Problem-Solution');
  if (/case study|success story|customer/i.test(body)) strategies.push('Social Proof');
  if (/•|\*|-|1\.|2\./.test(body)) strategies.push('Structured/Bullets');
  if (/learn|discover|find out|explore/i.test(body)) strategies.push('Educational');
  if (/award|leader|best|top|trusted/i.test(body)) strategies.push('Authority');
  if (/save|roi|return|revenue|profit/i.test(body)) strategies.push('ROI-focused');
  if (/easy|simple|quick|seamless/i.test(body)) strategies.push('Ease of Use');
  if (/integration|compatible|works with/i.test(body)) strategies.push('Integration');
  
  return strategies;
};

// Analyze CTA effectiveness
const analyzeCtaEffectiveness = (cta: string): string => {
  const ctaLower = cta.toLowerCase();
  
  if (/demo|see it|watch|view/i.test(ctaLower)) return 'Demo/Visual';
  if (/download|get|access/i.test(ctaLower)) return 'Resource Access';
  if (/learn|discover|explore/i.test(ctaLower)) return 'Educational';
  if (/sign up|register|join/i.test(ctaLower)) return 'Registration';
  if (/try|start|begin/i.test(ctaLower)) return 'Trial/Start';
  if (/contact|talk|speak/i.test(ctaLower)) return 'Contact Sales';
  
  return 'Other';
};

const AdAnalysisSummary: React.FC<AdAnalysisSummaryProps> = ({ ads }) => {
  // Enhanced ad analysis with more detailed insights
  const adAnalysis = React.useMemo(() => {
    const formats = new Set(ads.map(ad => ad.format));
    const ctas = new Set(ads.filter(ad => ad.ctas).map(ad => ad.ctas));
    const regions = new Set(ads.filter(ad => ad.targeting_location).map(ad => ad.targeting_location));
    
    let allThemes: string[] = [];
    let allHeadlinePatterns: string[] = [];
    let allCopyStrategies: string[] = [];
    let ctaTypes: string[] = [];
    
    ads.forEach(ad => {
      const bodyThemes = extractThemes(ad.body);
      const headlineThemes = extractThemes(ad.headline);
      allThemes = [...allThemes, ...bodyThemes, ...headlineThemes];
      
      allHeadlinePatterns = [...allHeadlinePatterns, ...analyzeHeadlinePatterns(ad.headline)];
      allCopyStrategies = [...allCopyStrategies, ...analyzeCopyStrategies(ad.body)];
      if (ad.ctas) ctaTypes.push(analyzeCtaEffectiveness(ad.ctas));
    });
    
    // Count frequencies
    const countFrequency = (arr: string[]) => {
      return arr.reduce((acc, item) => {
        acc[item] = (acc[item] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
    };

    const themeFrequency = countFrequency(allThemes);
    const headlinePatternFrequency = countFrequency(allHeadlinePatterns);
    const copyStrategyFrequency = countFrequency(allCopyStrategies);
    const ctaTypeFrequency = countFrequency(ctaTypes);

    // Sort by frequency
    const sortByFrequency = (obj: Record<string, number>) => 
      Object.entries(obj)
        .sort(([,a], [,b]) => b - a)
        .map(([item]) => item);

    return {
      formats: Array.from(formats),
      ctas: Array.from(ctas),
      regions: Array.from(regions),
      themes: sortByFrequency(themeFrequency),
      headlinePatterns: sortByFrequency(headlinePatternFrequency),
      copyStrategies: sortByFrequency(copyStrategyFrequency),
      ctaTypes: sortByFrequency(ctaTypeFrequency),
      totalAds: ads.length,
      averageBodyLength: Math.round(ads.reduce((sum, ad) => sum + ad.body.length, 0) / ads.length),
      averageHeadlineLength: Math.round(ads.reduce((sum, ad) => sum + ad.headline.length, 0) / ads.length)
    };
  }, [ads]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mt-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        📊 LinkedIn Ads Analysis
      </h2>

      <div className="space-y-8">
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
          <div className="space-y-6">
            <div className="flex items-start">
              <MessageSquare className="w-5 h-5 text-blue-500 mt-1 mr-3" />
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300">Headline Patterns</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {adAnalysis.headlinePatterns.map((pattern, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200"
                    >
                      {pattern}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <Layout className="w-5 h-5 text-blue-500 mt-1 mr-3" />
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300">Copy Strategies</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {adAnalysis.copyStrategies.map((strategy, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                    >
                      {strategy}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <Target className="w-5 h-5 text-blue-500 mt-1 mr-3" />
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300">CTA Analysis</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {adAnalysis.ctaTypes.map((type, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <Globe className="w-5 h-5 text-blue-500 mt-1 mr-3" />
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300">Geographic Focus</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {adAnalysis.regions.length > 0 ? adAnalysis.regions.map((region, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"
                    >
                      {region}
                    </span>
                  )) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">Global/Not specified</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            💡 Key Insights
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-4">Content Strategy</h4>
              <ul className="space-y-2">
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Average headline length: {adAnalysis.averageHeadlineLength} characters
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Average body length: {adAnalysis.averageBodyLength} characters
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Primary theme: {adAnalysis.themes[0] || 'N/A'}
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-4">Ad Format Analysis</h4>
              <ul className="space-y-2">
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  Most used format: {adAnalysis.formats[0] || 'N/A'}
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  Primary CTA type: {adAnalysis.ctaTypes[0] || 'N/A'}
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  {adAnalysis.formats.length} different formats used
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-4">Recommendations</h4>
              <ul className="space-y-2">
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  Test {adAnalysis.headlinePatterns.slice(-1)[0]} headlines
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  Incorporate more {adAnalysis.copyStrategies.slice(-1)[0]} messaging
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  Experiment with {adAnalysis.ctaTypes.slice(-1)[0]} CTAs
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdAnalysisSummary;